/**
 * This file exposes frequently used search methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import { type RV_TYPES, MOTORIZED_RV_TYPES, TOWABLE_RV_TYPES } from '~/constants/rv'
import {
  BUDGET_FRIENDLY_MAX_PRICE,
  BUDGET_FRIENDLY_MIN_PRICE,
} from '~/constants/search'
import type { SearchData, SearchRentalType } from '~/types/search'

/**
 * Checks if the price range is budget friendly.
 *
 * @param minPrice The minimum price.
 * @param maxPrice The maximum price.
 * @returns True if the price range is budget friendly, false otherwise.
 */
export function isBudgetFriendly(minPrice?: number, maxPrice?: number): boolean {
  return minPrice === BUDGET_FRIENDLY_MIN_PRICE && maxPrice === BUDGET_FRIENDLY_MAX_PRICE
}

/**
 * Expresses an applied search as the coarse rental type the search form offers. A
 * granular RV type selection has no equivalent in that form, so it reads as no
 * selection at all.
 *
 * @param search The delivery flag and RV types of the applied search.
 * @returns The matching rental type, or undefined when none matches.
 */
export function getAppliedRentalType(search: {
  delivery: boolean
  types: readonly RV_TYPES[]
}): SearchRentalType | undefined {
  if (search.delivery) {
    return 'delivery'
  }

  function isWholeCategory(categoryTypes: readonly RV_TYPES[]) {
    return search.types.length === categoryTypes.length
      && categoryTypes.every((type) => search.types.includes(type))
  }

  if (isWholeCategory(MOTORIZED_RV_TYPES)) {
    return 'drivable'
  }

  if (isWholeCategory(TOWABLE_RV_TYPES)) {
    return 'towable'
  }

  return undefined
}

/**
 * Maps GIS `name`/`code` onto city, region, and country fields when the place
 * type is set. Leaves GIS-populated values in place when `name` or `code` is
 * missing, matching search API mapping that treats `code` as optional.
 * @param location The location to normalize.
 * @returns The normalized location.
 */
export function getNormalizedSearchLocationParams(location: SearchData['location']) {
  const type = location?.type

  if (type === 'Country') {
    return {
      ...location,
      ...(location?.code ? { countryCode: location.code } : {}),
      ...(location?.name ? { country: location.name } : {}),
    }
  }

  if (type === 'Region') {
    return {
      ...location,
      ...(location?.code ? { regionCode: location.code } : {}),
      ...(location?.name ? { region: location.name } : {}),
    }
  }

  if (type === 'City') {
    return {
      ...location,
      ...(location?.name ? { city: location.name } : {}),
    }
  }

  return location
}
